import React, { createContext, useState, useContext, useMemo } from "react";
import datasetsFromFile from "../components/static/datasets.json";
import characteristicsList from "../components/static/characteristics.json";
import { getLocalStorageEnumMap } from "../services/user/browserService";

/**
 * SearchContext - Unified context for all search-related state
 *
 * Consolidates:
 * - DatasetContext (selectedDataset, datasetMap)
 * - ApplicationContext (enumMap, searchString, triggers)
 * - FilteredPeopleContext (search results, match source)
 * - SelectedCharacteristicsContext (available characteristics)
 * - TriggerContext (unused, removed)
 *
 * This reduces context overhead and prevents unnecessary re-renders
 * by keeping related state together.
 */
export const SearchContext = createContext();

export const SearchProvider = ({ children }) => {
  // ==================== Dataset State (from DatasetContext) ====================
  const [datasets] = useState(datasetsFromFile);

  // Initialize selected dataset from localStorage
  const localEnumMap = getLocalStorageEnumMap();
  let currentDataset = localEnumMap.get("Dataset");
  if (!currentDataset) {
    currentDataset = datasets[0];
  }

  const [selectedDataset, setSelectedDataset] = useState(currentDataset);

  // Initialize datasetMap
  const dsm = new Map();
  datasets.forEach((dataset) => {
    dsm.set(dataset, new Map());
  });
  dsm.set(selectedDataset, localEnumMap);

  const [datasetMap, setDatasetMap] = useState(dsm);

  // ==================== Search State (from ApplicationContext) ====================
  // Initialize enumMap from localStorage
  const [enumMap, setEnumMap] = useState(() => {
    const characteristicsMap = getLocalStorageEnumMap();
    characteristicsMap.set("Dataset", selectedDataset);
    return characteristicsMap;
  });

  const [defaultMap] = useState(() => {
    const characteristicsMap = getLocalStorageEnumMap();
    characteristicsMap.set("Dataset", selectedDataset);
    return characteristicsMap;
  });

  const [searchString, setSearchString] = useState();
  const [triggerSearch, setTriggerSearch] = useState(null);
  const [triggerAttributeSearch, setTriggerAttributeSearch] = useState(null);

  // ==================== Search Results (from FilteredPeopleContext) ====================
  const [filteredPeople, setFilteredPeople] = useState([]);
  const [matchSource, setMatchSource] = useState('manual_filter'); // 'manual_filter' or 'face_match'
  const [matchAccuracy, setMatchAccuracy] = useState(null); // Match accuracy for face_match results
  const [capturedImageUrl, setCapturedImageUrl] = useState(null); // Captured image URL for display
  const [capturedImageS3Key, setCapturedImageS3Key] = useState(null); // S3 key of captured image

  // ==================== Characteristics (from SelectedCharacteristicsContext) ====================
  const [selectedCharacteristics, setSelectedCharacteristics] = useState(characteristicsList);

  // ==================== Context Value ====================
  // Memoize value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    // Dataset state
    selectedDataset,
    setSelectedDataset,
    datasets,
    datasetMap,
    setDatasetMap,

    // Search/filter state
    enumMap,
    setEnumMap,
    defaultMap,
    searchString,
    setSearchString,
    triggerSearch,
    setTriggerSearch,
    triggerAttributeSearch,
    setTriggerAttributeSearch,

    // Search results
    filteredPeople,
    setFilteredPeople,
    matchSource,
    setMatchSource,
    matchAccuracy,
    setMatchAccuracy,
    capturedImageUrl,
    setCapturedImageUrl,
    capturedImageS3Key,
    setCapturedImageS3Key,

    // Characteristics
    selectedCharacteristics,
    setSelectedCharacteristics,
  }), [
    selectedDataset,
    datasets,
    datasetMap,
    enumMap,
    defaultMap,
    searchString,
    triggerSearch,
    triggerAttributeSearch,
    filteredPeople,
    matchSource,
    matchAccuracy,
    capturedImageUrl,
    capturedImageS3Key,
    selectedCharacteristics
  ]);

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  );
};

/**
 * Custom hook to use the SearchContext
 * @returns {Object} Search context value
 */
export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};

// Backward compatibility exports (to be removed after migration)
export const DatasetContext = SearchContext;
export const DatasetProvider = SearchProvider;
export const ApplicationContext = SearchContext;
export const ApplicationProvider = SearchProvider;
export const FilteredPeopleContext = SearchContext;
export const FilteredPeopleProvider = SearchProvider;
export const SelectedCharacteristicsContext = SearchContext;
export const SelectedCharacteristicsProvider = SearchProvider;
